import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  Shield,
  FileText,
  Lock,
  Eye,
  AlertCircle,
  CheckCircle,
  MessageCircle,
  Phone,
  Info,
  Clock,
  Users
} from 'lucide-react';

const Policies = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center shadow-elegant">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-hero text-foreground">Privacy & Policies</h1>
            <p className="text-xl text-primary font-medium">Your privacy and rights are important to us</p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We are committed to protecting your personal information and ensuring transparency in how we 
              operate our wellness services in compliance with South African law.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Lock className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">POPIA Compliant</h3>
                <p className="text-sm text-muted-foreground">
                  Full compliance with South Africa's Protection of Personal Information Act
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Eye className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Transparent</h3>
                <p className="text-sm text-muted-foreground">
                  Clear information about how we collect, use, and protect your data
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Secure</h3>
                <p className="text-sm text-muted-foreground">
                  Advanced security measures to protect your personal information
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Privacy Policy */}
          <div className="max-w-4xl mx-auto space-y-12">
            <Card className="shadow-elegant border-0">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Privacy Policy & POPIA Notice</h2>
                </div>
                
                <div className="space-y-6 text-muted-foreground">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Information We Collect</h3>
                    <p className="mb-3">
                      Glow by Precious collects personal information necessary to provide our wellness services effectively and safely:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li>• <strong>Contact Information:</strong> Name, phone number, email address, physical address</li>
                      <li>• <strong>Health Information:</strong> Medical history relevant to treatments, current medications, allergies</li>
                      <li>• <strong>Treatment Records:</strong> Services received, progress notes, before/after measurements</li>
                      <li>• <strong>Communication Records:</strong> WhatsApp messages, phone calls, appointment bookings</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">How We Use Your Information</h3>
                    <p className="mb-3">Your personal information is used solely for legitimate business purposes:</p>
                    <ul className="space-y-2 ml-6">
                      <li>• Providing safe and effective wellness treatments</li>
                      <li>• Scheduling and managing appointments</li>
                      <li>• Monitoring treatment progress and results</li>
                      <li>• Communicating about your treatments and care</li>
                      <li>• Ensuring compliance with health and safety regulations</li>
                      <li>• Improving our services based on client feedback</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Information Protection</h3>
                    <p className="mb-3">We implement comprehensive security measures to protect your personal information:</p>
                    <ul className="space-y-2 ml-6">
                      <li>• Secure storage of physical and digital records</li>
                      <li>• Limited access to authorized personnel only</li>
                      <li>• Regular security reviews and updates</li>
                      <li>• Confidentiality agreements with all staff members</li>
                      <li>• Secure disposal of outdated records</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Your Rights Under POPIA</h3>
                    <p className="mb-3">As a data subject, you have the following rights:</p>
                    <ul className="space-y-2 ml-6">
                      <li>• <strong>Access:</strong> Request copies of your personal information</li>
                      <li>• <strong>Correction:</strong> Request correction of inaccurate information</li>
                      <li>• <strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                      <li>• <strong>Objection:</strong> Object to processing of your personal information</li>
                      <li>• <strong>Portability:</strong> Request transfer of your information to another provider</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Information Sharing</h3>
                    <p>
                      We do not sell, trade, or share your personal information with third parties except when:
                    </p>
                    <ul className="space-y-2 ml-6 mt-3">
                      <li>• Required by law or legal process</li>
                      <li>• Necessary for medical emergencies</li>
                      <li>• You have provided explicit consent</li>
                      <li>• Working with trusted service providers under confidentiality agreements</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Terms & Conditions */}
            <Card className="shadow-elegant border-0">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Terms & Conditions</h2>
                </div>
                
                <div className="space-y-6 text-muted-foreground">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Service Agreement</h3>
                    <p className="mb-3">
                      By booking and receiving services at Glow by Precious, you agree to the following terms:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li>• All treatments are provided for wellness and aesthetic purposes only</li>
                      <li>• We do not provide medical diagnosis or treatment for medical conditions</li>
                      <li>• Results may vary between individuals and are not guaranteed</li>
                      <li>• You must disclose all relevant health information before treatment</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Booking & Cancellation Policy</h3>
                    <ul className="space-y-2 ml-6">
                      <li>• <strong>Advance Booking:</strong> Appointments should be booked in advance</li>
                      <li>• <strong>Cancellation Notice:</strong> 24-hour notice required for cancellations</li>
                      <li>• <strong>Late Cancellations:</strong> Same-day cancellations may incur fees</li>
                      <li>• <strong>No-Shows:</strong> Full treatment fee may be charged for no-shows</li>
                      <li>• <strong>Rescheduling:</strong> Free rescheduling with 24-hour notice</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Treatment Disclaimers</h3>
                    <ul className="space-y-2 ml-6">
                      <li>• Individual results may vary based on personal factors</li>
                      <li>• Multiple sessions may be required for optimal results</li>
                      <li>• Lifestyle factors can affect treatment outcomes</li>
                      <li>• Some treatments may not be suitable for certain health conditions</li>
                      <li>• We reserve the right to refuse treatment if deemed inappropriate</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Payment Terms</h3>
                    <ul className="space-y-2 ml-6">
                      <li>• Payment is due at the time of service unless otherwise arranged</li>
                      <li>• Package deals must be paid in full before first treatment</li>
                      <li>• Packages are non-refundable but may be transferred with notice</li>
                      <li>• Special offers cannot be combined with other promotions</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Liability Limitations</h3>
                    <p className="mb-3">
                      While we maintain comprehensive insurance and safety protocols:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li>• Clients participate in treatments at their own risk</li>
                      <li>• We are not liable for results that don't meet expectations</li>
                      <li>• Our liability is limited to the cost of services provided</li>
                      <li>• Clients must follow all pre and post-treatment instructions</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cookie & Data Notice */}
            <Card className="shadow-elegant border-0">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <Info className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Cookie & Data Notice</h2>
                </div>
                
                <div className="space-y-6 text-muted-foreground">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Website Data Collection</h3>
                    <p className="mb-3">
                      Our website may collect limited data to improve your browsing experience:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li>• <strong>Essential Cookies:</strong> Required for website functionality</li>
                      <li>• <strong>Analytics:</strong> Anonymous usage statistics to improve our website</li>
                      <li>• <strong>Contact Forms:</strong> Information you voluntarily provide</li>
                      <li>• <strong>Communication:</strong> WhatsApp and phone interactions</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Third-Party Services</h3>
                    <p className="mb-3">
                      We may use trusted third-party services that have their own privacy policies:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li>• WhatsApp for communication (Meta's privacy policy applies)</li>
                      <li>• Website hosting and security services</li>
                      <li>• Payment processing (if applicable)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Data Retention</h3>
                    <p>
                      We retain your personal information only as long as necessary for:
                    </p>
                    <ul className="space-y-2 ml-6 mt-3">
                      <li>• Providing ongoing wellness services</li>
                      <li>• Meeting legal and regulatory requirements</li>
                      <li>• Maintaining treatment records for continuity of care</li>
                      <li>• Resolving any disputes or issues</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact for Privacy Matters */}
            <Card className="shadow-elegant border-2 border-primary/20">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground">Privacy Questions or Concerns?</h2>
                  </div>
                  
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    If you have any questions about our privacy practices, want to exercise your POPIA rights, 
                    or have concerns about how we handle your personal information, please contact us.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 justify-center">
                        <MessageCircle className="h-5 w-5 text-primary" />
                        <span className="font-medium text-foreground">WhatsApp</span>
                      </div>
                      <p className="text-muted-foreground">069 552 3598</p>
                      <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                        <Button className="gradient-primary text-white w-full">
                          Message Us
                        </Button>
                      </a>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 justify-center">
                        <Phone className="h-5 w-5 text-primary" />
                        <span className="font-medium text-foreground">Phone</span>
                      </div>
                      <p className="text-muted-foreground">069 552 3598</p>
                      <a href="tel:0695523598">
                        <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white w-full">
                          Call Us
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Last Updated */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="text-sm">Last updated: December 2024</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
                We may update these policies from time to time. Any changes will be communicated to our clients 
                and posted on our website. Continued use of our services constitutes acceptance of updated terms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Your Privacy is Protected</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Feel confident knowing that your personal information is secure and handled with the utmost care 
                at Glow by Precious.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Book with Confidence
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Policies;